import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Avatar,
  Paper,
  Chip,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  Refresh,
  Download,
  Palette,
  Face,
  Pets,
  SmartToy
} from '@mui/icons-material';

// Configuraciones de estilos disponibles para personalización
const AVATAR_STYLES = {
  personas: {
    name: 'Personas',
    icon: Face,
    apiStyle: 'avataaars',
    color: '#2196F3',
    options: {
      topType: [
        'NoHair', 'Eyepatch', 'Hat', 'Hijab', 'Turban', 'WinterHat1', 'WinterHat2', 'WinterHat3', 'WinterHat4',
        'LongHairBigHair', 'LongHairBob', 'LongHairBun', 'LongHairCurly', 'LongHairCurvy', 'LongHairDreads',
        'LongHairFrida', 'LongHairFro', 'LongHairFroBand', 'LongHairNotTooLong', 'LongHairShavedSides',
        'LongHairMiaWallace', 'LongHairStraight', 'LongHairStraight2', 'LongHairStraightStrand',
        'ShortHairDreads01', 'ShortHairDreads02', 'ShortHairFrizzle', 'ShortHairShaggyMullet',
        'ShortHairShortCurly', 'ShortHairShortFlat', 'ShortHairShortRound', 'ShortHairShortWaved',
        'ShortHairSides', 'ShortHairTheCaesar', 'ShortHairTheCaesarSidePart'
      ],
      accessoriesType: [
        'Blank', 'Kurt', 'Prescription01', 'Prescription02', 'Round', 'Sunglasses', 'Wayfarers'
      ],
      hairColor: [
        'Auburn', 'Black', 'Blonde', 'BlondeGolden', 'Brown', 'BrownDark', 'PastelPink', 'Platinum', 'Red', 'SilverGray'
      ],
      facialHairType: [
        'Blank', 'BeardMedium', 'BeardLight', 'BeardMagestic', 'MoustacheFancy', 'MoustacheMagnum'
      ],
      clotheType: [
        'BlazerShirt', 'BlazerSweater', 'CollarSweater', 'GraphicShirt', 'Hoodie', 'Overall', 'ShirtCrewNeck',
        'ShirtScoopNeck', 'ShirtVNeck'
      ],
      eyeType: [
        'Close', 'Cry', 'Default', 'Dizzy', 'EyeRoll', 'Happy', 'Hearts', 'Side', 'Squint', 'Surprised',
        'Wink', 'WinkWacky'
      ],
      eyebrowType: [
        'Angry', 'AngryNatural', 'Default', 'DefaultNatural', 'FlatNatural', 'RaisedExcited',
        'RaisedExcitedNatural', 'SadConcerned', 'SadConcernedNatural', 'UnibrowNatural', 'UpDown',
        'UpDownNatural'
      ],
      mouthType: [
        'Concerned', 'Default', 'Disbelief', 'Eating', 'Grimace', 'Sad', 'ScreamOpen', 'Serious',
        'Smile', 'Tongue', 'Twinkle', 'Vomit'
      ],
      skinColor: [
        'Tanned', 'Yellow', 'Pale', 'Light', 'Brown', 'DarkBrown', 'Black'
      ]
    }
  },
  robots: {
    name: 'Robots',
    icon: SmartToy,
    apiStyle: 'bottts',
    color: '#9C27B0',
    options: {
      colors: ['blue', 'green', 'red', 'orange', 'yellow', 'purple', 'pink', 'gray'],
      colorful: [true, false]
    }
  },
  abstractos: {
    name: 'Abstractos',
    icon: Palette,
    apiStyle: 'identicon',
    color: '#FF5722',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
    }
  }
};

const AvatarCreator = ({
  open,
  onClose,
  onSave
}) => {
  const theme = useTheme();

  const [selectedStyle, setSelectedStyle] = useState('personas');
  const [customName, setCustomName] = useState('');
  const [options, setOptions] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  // Generar URL del avatar basado en configuración actual
  const avatarUrl = useMemo(() => {
    if (!selectedStyle) return '';

    const styleConfig = AVATAR_STYLES[selectedStyle];
    const baseUrl = `https://api.dicebear.com/7.x/${styleConfig.apiStyle}/png`;

    // Construir parámetros de la URL
    const params = new URLSearchParams();

    // Usar nombre personalizado o generar uno aleatorio
    const seed = customName || `custom-${Date.now()}`;
    params.set('seed', seed);

    // Agregar opciones específicas del estilo
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, value);
      }
    });

    return `${baseUrl}?${params.toString()}`;
  }, [selectedStyle, customName, options]);

  // Manejar cambio de estilo
  const handleStyleChange = (newStyle) => {
    setSelectedStyle(newStyle);
    setOptions({}); // Reset opciones cuando cambien de estilo
  };

  // Manejar cambio de opción
  const handleOptionChange = (optionKey, value) => {
    setOptions(prev => ({
      ...prev,
      [optionKey]: value
    }));
  };

  // Generar avatar aleatorio
  const generateRandom = () => {
    const styleConfig = AVATAR_STYLES[selectedStyle];
    const newOptions = {};

    // Generar valores aleatorios para cada opción
    Object.entries(styleConfig.options).forEach(([key, possibleValues]) => {
      if (Array.isArray(possibleValues) && possibleValues.length > 0) {
        const randomIndex = Math.floor(Math.random() * possibleValues.length);
        newOptions[key] = possibleValues[randomIndex];
      }
    });

    setOptions(newOptions);
    setCustomName(`avatar-${Date.now()}`);
  };

  // Guardar avatar
  const handleSave = async () => {
    if (!avatarUrl) return;

    setIsGenerating(true);
    try {
      // Descargar el avatar
      const response = await fetch(avatarUrl);
      const blob = await response.blob();
      const file = new File([blob], `${customName || 'custom-avatar'}.png`, { type: 'image/png' });

      if (onSave) {
        onSave(URL.createObjectURL(file), file);
      }

      onClose();
    } catch (error) {
      console.error('Error saving custom avatar:', error);
      alert('Error al guardar el avatar personalizado');
    } finally {
      setIsGenerating(false);
    }
  };

  const currentStyle = AVATAR_STYLES[selectedStyle];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 1 }}>
          🎨 Crear Avatar Personalizado
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Personaliza tu avatar con diferentes estilos y opciones
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {/* Panel izquierdo - Vista previa */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                backgroundColor: alpha(currentStyle?.color || theme.palette.primary.main, 0.05)
              }}
            >
              <Typography variant="h6" gutterBottom>
                Vista Previa
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto',
                    border: `3px solid ${currentStyle?.color || theme.palette.primary.main}`,
                    boxShadow: 3
                  }}
                />
              </Box>

              <TextField
                fullWidth
                label="Nombre del Avatar"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Ej: mi-avatar-personalizado"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={generateRandom}
                sx={{
                  borderColor: currentStyle?.color,
                  color: currentStyle?.color,
                  '&:hover': {
                    borderColor: currentStyle?.color,
                    backgroundColor: alpha(currentStyle?.color || theme.palette.primary.main, 0.1)
                  }
                }}
              >
                Generar Aleatorio
              </Button>
            </Paper>
          </Grid>

          {/* Panel derecho - Configuraciones */}
          <Grid item xs={12} md={8}>
            <Box>
              {/* Selector de estilo */}
              <Typography variant="h6" gutterBottom>
                Estilo de Avatar
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {Object.entries(AVATAR_STYLES).map(([key, style]) => {
                  const IconComponent = style.icon;
                  return (
                    <Grid item key={key}>
                      <Chip
                        icon={<IconComponent />}
                        label={style.name}
                        onClick={() => handleStyleChange(key)}
                        variant={selectedStyle === key ? 'filled' : 'outlined'}
                        sx={{
                          backgroundColor: selectedStyle === key ? style.color : 'transparent',
                          color: selectedStyle === key ? 'white' : style.color,
                          borderColor: style.color,
                          '&:hover': {
                            backgroundColor: alpha(style.color, 0.1)
                          }
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Opciones específicas del estilo */}
              <Typography variant="h6" gutterBottom>
                Personalización
              </Typography>

              <Grid container spacing={2}>
                {Object.entries(currentStyle?.options || {}).map(([optionKey, possibleValues]) => (
                  <Grid item xs={12} sm={6} md={4} key={optionKey}>
                    <FormControl fullWidth size="small">
                      <InputLabel>
                        {optionKey.charAt(0).toUpperCase() + optionKey.slice(1).replace(/([A-Z])/g, ' $1')}
                      </InputLabel>
                      <Select
                        value={options[optionKey] || ''}
                        onChange={(e) => handleOptionChange(optionKey, e.target.value)}
                        label={optionKey.charAt(0).toUpperCase() + optionKey.slice(1).replace(/([A-Z])/g, ' $1')}
                      >
                        <MenuItem value="">
                          <em>Aleatorio</em>
                        </MenuItem>
                        {possibleValues.map((value) => (
                          <MenuItem key={value} value={value}>
                            {typeof value === 'boolean' ?
                              (value ? 'Sí' : 'No') :
                              value.charAt(0).toUpperCase() + value.slice(1).replace(/([A-Z])/g, ' $1')
                            }
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleSave}
          disabled={isGenerating || !avatarUrl}
          sx={{
            backgroundColor: currentStyle?.color || theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(currentStyle?.color || theme.palette.primary.main, 0.8)
            }
          }}
        >
          {isGenerating ? 'Creando...' : 'Usar Este Avatar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCreator;